/**
 * Buysaavy theme — vanilla JS behavior (no build step, no framework).
 *
 * Cart drawer, mobile menu, add-to-cart, cart quantity steppers, product
 * gallery / tabs / variant picker, collection sort + filters toggle, and the
 * Help Center FAQ filter.
 *
 * Cart mutations use Shopify's Ajax Cart API (cart/add.js, cart/change.js)
 * and then re-render the cart drawer, the header cart count and (on the
 * /cart page) the cart page itself, so every cart surface stays in sync.
 */
(function () {
  'use strict';

  const config = window.buysaavy || {};
  const cfgRoutes = config.routes || {};
  const routes = {
    cartAdd: cfgRoutes.cartAdd || '/cart/add.js',
    cartChange: cfgRoutes.cartChange || '/cart/change.js',
    cart: cfgRoutes.cart || '/cart',
    discount: cfgRoutes.discount || '/discount/',
    root: cfgRoutes.root || '/',
  };

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }
  function qsa(selector, scope) {
    return Array.from((scope || document).querySelectorAll(selector));
  }

  function showToast(message) {
    if (!message) return;
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);
    window.setTimeout(() => toast.classList.add('is-visible'), 10);
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 300);
    }, 4500);
  }

  function syncDrawerTriggers(id, open) {
    qsa('[data-drawer-open="' + id + '"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function openDrawer(id) {
    const overlay = qs('#' + id + '-overlay');
    if (!overlay) return;
    overlay.setAttribute('data-state', 'open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    syncDrawerTriggers(id, true);
    const panel = overlay.querySelector('aside');
    const focusable = panel
      ? panel.querySelector('[data-drawer-close], a, button, input, select, textarea')
      : null;
    if (focusable) focusable.focus({preventScroll: true});
  }

  function closeAllDrawers() {
    qsa('.overlay[data-state="open"]').forEach((overlay) => {
      overlay.setAttribute('data-state', 'closed');
      overlay.setAttribute('aria-hidden', 'true');
      const id = (overlay.id || '').replace(/-overlay$/, '');
      if (id) syncDrawerTriggers(id, false);
    });
    document.body.classList.remove('drawer-open');
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[name="checkout"]')) return;

    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      const isLink = closer.tagName === 'A' && closer.getAttribute('href');
      if (!isLink) event.preventDefault();
      closeAllDrawers();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllDrawers();
  });

  function placeHeaderMenus() {
    const header = qs('.site-header');
    if (!header) return;
    const bottom = Math.round(header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--nav-menu-top', bottom + 'px');
  }

  function closeHeaderMenus() {
    qsa('.site-header__nav .nav-item.is-open').forEach((item) => item.classList.remove('is-open'));
  }

  placeHeaderMenus();
  window.addEventListener('resize', placeHeaderMenus);
  window.addEventListener('scroll', placeHeaderMenus, {passive: true});

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-mobile-nav-toggle]');
    if (toggle) {
      event.preventDefault();
      const group = toggle.closest('.mobile-nav__group');
      const wasOpen = group.classList.contains('is-open');
      qsa('.mobile-nav__group.is-open').forEach((item) => {
        item.classList.remove('is-open');
        const button = item.querySelector('[data-mobile-nav-toggle]');
        if (button) button.setAttribute('aria-expanded', 'false');
      });
      if (!wasOpen) {
        group.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (window.matchMedia('(min-width: 1024px)').matches) return;
    const trigger = event.target.closest('.site-header__nav .nav-item.has-dropdown > a');
    if (trigger) {
      const item = trigger.parentElement;
      const wasOpen = item.classList.contains('is-open');
      closeHeaderMenus();
      if (!wasOpen) {
        event.preventDefault();
        placeHeaderMenus();
        item.classList.add('is-open');
      }
      return;
    }
    if (!event.target.closest('.nav-dropdown')) closeHeaderMenus();
  });

  function refreshCartSections() {
    const sectionsRequest = fetch(`${routes.cart}?sections=cart-drawer,header-cart-bubble`, {cache: 'no-store'})
      .then((res) => res.json())
      .then((sections) => {
        const drawerHtml = sections['cart-drawer'];
        const bubbleHtml = sections['header-cart-bubble'];
        if (drawerHtml) {
          const drawerTarget = qs('#cart-drawer-body');
          const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
          const fresh = doc.querySelector('#cart-drawer-body');
          if (drawerTarget && fresh) drawerTarget.innerHTML = fresh.innerHTML;
        }
        if (bubbleHtml) {
          const bubbleTarget = qs('#header-cart-bubble');
          const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const fresh = doc.querySelector('#header-cart-bubble');
          if (bubbleTarget && fresh) bubbleTarget.outerHTML = fresh.outerHTML;
        }
      });

    const pageTarget = qs('#cart-page-content');
    const pageRequest = pageTarget
      ? fetch(routes.cart, {cache: 'no-store'})
          .then((res) => res.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('#cart-page-content');
            if (fresh) pageTarget.innerHTML = fresh.innerHTML;
          })
      : Promise.resolve();

    return Promise.all([sectionsRequest, pageRequest]).catch(() => {
      window.location.reload();
    });
  }

  function setFormError(form, message) {
    const errorEl = form.querySelector('[data-add-to-cart-error]');
    if (errorEl) errorEl.textContent = message || '';
    else if (message) showToast(message);
  }

  function isNativeCheckoutSubmit(event, form) {
    const submitter = event.submitter;
    if (!submitter) return false;
    if (submitter.getAttribute('name') === 'checkout') return true;
    if (submitter.closest('.shopify-payment-button')) return true;
    if (form.querySelector('.shopify-payment-button') && submitter.type === 'submit' && !submitter.hasAttribute('data-add-to-cart-submit')) {
      return submitter.name !== 'add' && !submitter.hasAttribute('data-add-to-cart-submit');
    }
    return false;
  }

  document.addEventListener('submit', (event) => {
    if (event.submitter && event.submitter.getAttribute('name') === 'checkout') return;
    if (event.target && event.target.hasAttribute('data-cart-checkout-form')) return;
    if (event.target && event.target.id === 'cart') return;
    if (event.target && event.target.id === 'CartDrawer-Form') return;
    const form = event.target.closest('form[data-add-to-cart-form]');
    if (!form) return;
    if (isNativeCheckoutSubmit(event, form)) return;

    event.preventDefault();
    const submitBtn = form.querySelector('[data-add-to-cart-submit], button[name="add"], button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    setFormError(form, '');

    fetch(routes.cartAdd, {
      method: 'POST',
      headers: {Accept: 'application/json'},
      body: new FormData(form),
    })
      .then((res) => res.json().then((body) => ({ok: res.ok, body})))
      .then((result) => {
        if (!result.ok || result.body.status) {
          setFormError(form, result.body.description || result.body.message || 'This item could not be added to your cart.');
          return;
        }
        if (document.body.dataset.cartType === 'page') {
          window.location.href = routes.cart;
          return;
        }
        return refreshCartSections().then(() => openDrawer('cart'));
      })
      .catch(() => setFormError(form, 'Something went wrong. Please try again.'))
      .finally(() => {
        if (submitBtn && !submitBtn.hasAttribute('data-locked')) submitBtn.disabled = false;
      });
  });

  let cartBusy = false;

  document.addEventListener('click', (event) => {
    const stepper = event.target.closest('[data-cart-qty-step]');
    const remover = event.target.closest('[data-cart-remove]');
    if (!stepper && !remover) return;
    event.preventDefault();

    const lineEl = (stepper || remover).closest('.cart-line');
    if (!lineEl) return;
    const key = lineEl.getAttribute('data-key');
    const line = lineEl.getAttribute('data-line');
    const current = parseInt(lineEl.getAttribute('data-quantity'), 10) || 0;

    if (remover) {
      updateCartLine(key, line, 0);
    } else {
      const delta = parseInt(stepper.getAttribute('data-cart-qty-step'), 10) || 0;
      updateCartLine(key, line, Math.max(0, current + delta));
    }
  });

  function showLineError(line, message) {
    qsa(`.cart-line[data-line="${line}"] [data-cart-line-error]`).forEach((el) => {
      el.textContent = message;
      el.hidden = !message;
    });
  }

  function updateCartLine(key, line, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    const selector = key ? `.cart-line[data-key="${CSS.escape(key)}"]` : `.cart-line[data-line="${line}"]`;
    qsa(selector).forEach((el) => el.classList.add('is-updating'));

    const payload = key
      ? {id: key, quantity: Number(quantity)}
      : {line: Number(line), quantity: Number(quantity)};

    fetch(routes.cartChange, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(payload),
    })
      .then((res) => res.json().then((body) => ({ok: res.ok, body})))
      .then((result) =>
        refreshCartSections().then(() => {
          if (!result.ok || result.body.status) {
            showLineError(line, result.body.description || result.body.message || 'That quantity is not available.');
          }
        })
      )
      .catch(() => showToast('Could not update your cart. Please try again.'))
      .finally(() => {
        cartBusy = false;
        qsa('.cart-line.is-updating').forEach((el) => el.classList.remove('is-updating'));
      });
  }

  document.addEventListener('click', (event) => {
    const applyBtn = event.target.closest('[data-discount-apply]');
    if (!applyBtn) return;
    const wrap = applyBtn.closest('[data-discount-form]');
    if (!wrap) return;
    const field = wrap.querySelector('[name="discount"], [name="code"]');
    const code = ((field && field.value) || '').trim();
    if (!code) return;
    const base = routes.discount.endsWith('/') ? routes.discount : routes.discount + '/';
    window.location.href = `${base}${encodeURIComponent(code)}?redirect=${encodeURIComponent(routes.cart)}`;
  });

  function markInit(el) {
    if (!el || el.dataset.jsInit === 'true') return false;
    el.dataset.jsInit = 'true';
    return true;
  }

  function initAll(scope) {
    const root = scope || document;
    qsa('[data-gallery]', root).forEach(initGallery);
    qsa('[data-qty-field]', root).forEach(initQuantityField);
    qsa('[data-tabs]', root).forEach(initTabs);
    qsa('[data-collection-view-toggle]', root).forEach(initViewToggle);
    qsa('[data-product-form]', root).forEach(initVariantPicker);
    qsa('[data-product-recommendations]', root).forEach(initRecommendations);
  }

  document.addEventListener('DOMContentLoaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  function initVariantPicker(root) {
    if (!markInit(root)) return;
    const jsonEl = qs('[data-product-json]', root);
    if (!jsonEl) return;
    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    const labels = {
      add: root.getAttribute('data-label-add') || 'Add to Cart',
      soldOut: root.getAttribute('data-label-sold-out') || 'Sold Out',
      unavailable: root.getAttribute('data-label-unavailable') || 'Unavailable',
      save: root.getAttribute('data-label-save') || 'Save {percent}% OFF',
    };
    const fieldsets = qsa('.option-picker', root);
    const buybox = root.closest('.product-buybox') || root;
    const priceEl = qs('[data-product-price]', buybox);
    const compareAtEl = qs('[data-product-compare-at]', buybox);
    const savingsEl = qs('[data-product-savings]', buybox);
    const skuEl = qs('[data-product-sku]', buybox);
    const availabilityEl = qs('[data-product-availability]', buybox);
    const variantIdInput = qs('[data-selected-variant-id]', root);
    const submitBtn = qs('[data-add-to-cart-submit]', root);
    const submitLabel = qs('[data-add-to-cart-label]', root);
    const productSection = root.closest('.product-detail') || document;
    const gallery = qs('[data-gallery]', productSection);

    function selectedOptions() {
      const chosen = [null, null, null];
      fieldsets.forEach((fieldset) => {
        const position = parseInt(fieldset.getAttribute('data-option-position'), 10) - 1;
        const radio = qs('input[type="radio"]:checked', fieldset);
        const active = radio || qs('.option-value.is-selected', fieldset);
        const value = radio ? radio.value : active && active.getAttribute('data-option-value');
        if (position >= 0 && position < 3 && value) chosen[position] = value;
      });
      return chosen;
    }

    function findMatch(options) {
      return variants.find((variant) => {
        const values = [variant.option1, variant.option2, variant.option3];
        return values.every((value, i) => options[i] === null || value === options[i]);
      });
    }

    function lockButton(disabled, label) {
      if (submitBtn) {
        submitBtn.disabled = disabled;
        if (disabled) submitBtn.setAttribute('data-locked', '');
        else submitBtn.removeAttribute('data-locked');
      }
      if (submitLabel) submitLabel.textContent = label;
    }

    function applyVariant(variant) {
      if (!variant) {
        lockButton(true, labels.unavailable);
        return;
      }
      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceEl) priceEl.textContent = variant.price_formatted;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compareAtEl) {
        compareAtEl.textContent = onSale ? variant.compare_at_formatted : '';
        compareAtEl.hidden = !onSale;
      }
      if (savingsEl) {
        if (onSale) {
          const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          savingsEl.textContent = labels.save.replace('{percent}', percent);
        }
        savingsEl.hidden = !onSale;
      }
      if (skuEl) {
        skuEl.textContent = variant.sku ? 'SKU: ' + variant.sku : '';
        skuEl.hidden = !variant.sku;
      }
      if (availabilityEl) {
        availabilityEl.textContent = variant.available ? 'In stock' : labels.soldOut;
        availabilityEl.classList.toggle('is-sold-out', !variant.available);
      }
      lockButton(!variant.available, variant.available ? labels.add : labels.soldOut);

      if (variant.image && gallery) {
        gallery.dispatchEvent(new CustomEvent('gallery:show-src', {detail: variant.image}));
      }
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    root.addEventListener('click', (event) => {
      const btn = event.target.closest('.option-value');
      if (!btn) return;
      const fieldset = btn.closest('.option-picker');
      if (!fieldset) return;
      qsa('.option-value', fieldset).forEach((b) => b.classList.toggle('is-selected', b === btn));
      const radio = qs(`input[type="radio"][value="${CSS.escape(btn.getAttribute('data-option-value') || '')}"]`, fieldset);
      if (radio) radio.checked = true;
      applyVariant(findMatch(selectedOptions()));
    });

    root.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      const fieldset = event.target.closest('.option-picker');
      if (!fieldset) return;
      qsa('.option-value', fieldset).forEach((b) => {
        b.classList.toggle('is-selected', b.getAttribute('data-option-value') === event.target.value);
      });
      applyVariant(findMatch(selectedOptions()));
    });
  }

  function buildZoomViewer(slides, getActive, setActive) {
    const root = document.createElement('div');
    root.className = 'gallery-zoom';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Zoomed product image');
    root.innerHTML = [
      '<div class="gallery-zoom__backdrop" data-zoom-close></div>',
      '<div class="gallery-zoom__controls">',
      '<button type="button" data-zoom-out aria-label="Zoom out">−</button>',
      '<button type="button" data-zoom-in aria-label="Zoom in">+</button>',
      '<button type="button" data-zoom-close aria-label="Close">×</button>',
      '</div>',
      '<button type="button" class="gallery-zoom__nav gallery-zoom__nav--prev" data-zoom-prev aria-label="Previous image">‹</button>',
      '<button type="button" class="gallery-zoom__nav gallery-zoom__nav--next" data-zoom-next aria-label="Next image">›</button>',
      '<div class="gallery-zoom__stage" data-zoom-stage><img alt="" draggable="false"></div>'
    ].join('');
    document.body.appendChild(root);

    const stage = root.querySelector('[data-zoom-stage]');
    const img = stage.querySelector('img');
    const prev = root.querySelector('[data-zoom-prev]');
    const next = root.querySelector('[data-zoom-next]');
    let scale = 1;
    let tx = 0;
    let ty = 0;
    let index = 0;
    let drag = null;
    let openedAt = 0;
    const pointers = new Map();
    let pinch = null;

    function apply() {
      img.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
      stage.classList.toggle('is-zoomed', scale > 1);
    }

    function clampPan() {
      const maxX = ((scale - 1) * stage.clientWidth) / 2;
      const maxY = ((scale - 1) * stage.clientHeight) / 2;
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    }

    function setScale(nextScale) {
      scale = Math.max(1, Math.min(4, nextScale));
      if (scale === 1) {
        tx = 0;
        ty = 0;
      } else {
        clampPan();
      }
      apply();
    }

    let loadToken = 0;

    function slideImage(i) {
      const slide = slides[(i + slides.length) % slides.length];
      return slide ? slide.querySelector('img') : null;
    }

    function isVideoSlide(slide) {
      if (!slide) return false;
      const type = slide.getAttribute('data-media-type');
      return type === 'video' || type === 'external_video';
    }

    function load(i) {
      if (!slides.length) return;
      let cursor = ((i % slides.length) + slides.length) % slides.length;
      const step = i >= index ? 1 : -1;
      let hops = 0;
      while (isVideoSlide(slides[cursor]) && hops < slides.length) {
        cursor = (cursor + step + slides.length) % slides.length;
        hops += 1;
      }
      if (isVideoSlide(slides[cursor])) {
        close();
        return;
      }
      index = cursor;
      const slide = slides[index];
      const source = slide.querySelector('img');
      const shown = source && (source.currentSrc || source.src);
      const zoomSrc = slide.getAttribute('data-zoom-src');
      const token = ++loadToken;
      img.alt = (source && source.alt) || '';
      if (shown) img.src = shown;
      setScale(1);
      if (zoomSrc && zoomSrc !== shown) {
        const hi = new Image();
        hi.decoding = 'async';
        hi.onload = () => {
          if (token !== loadToken || root.hidden) return;
          img.src = zoomSrc;
        };
        hi.src = zoomSrc;
      }
      [-1, 1].forEach((step) => {
        const neighbor = slideImage(index + step);
        if (neighbor && neighbor.decode) neighbor.decode().catch(() => {});
      });
      const many = slides.length > 1;
      prev.hidden = !many;
      next.hidden = !many;
      if (setActive) setActive(index);
    }

    function open(i) {
      load(i);
      openedAt = Date.now();
      root.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function close() {
      root.hidden = true;
      document.body.style.overflow = '';
      setScale(1);
    }

    root.addEventListener('click', (event) => {
      const closing = event.target.closest('[data-zoom-close]');
      if (closing && Date.now() - openedAt < 400) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (closing) close();
      else if (event.target.closest('[data-zoom-in]')) setScale(scale * 1.35);
      else if (event.target.closest('[data-zoom-out]')) setScale(scale / 1.35);
      else if (event.target.closest('[data-zoom-prev]')) load(index - 1);
      else if (event.target.closest('[data-zoom-next]')) load(index + 1);
    });

    stage.addEventListener('dblclick', () => setScale(scale > 1 ? 1 : 2));
    stage.addEventListener('wheel', (event) => {
      if (root.hidden) return;
      event.preventDefault();
      setScale(scale * (event.deltaY < 0 ? 1.15 : 0.87));
    }, {passive: false});

    stage.addEventListener('pointerdown', (event) => {
      pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
      stage.setPointerCapture(event.pointerId);
      if (pointers.size === 1) {
        drag = {x: event.clientX, y: event.clientY, tx: tx, ty: ty};
        stage.classList.add('is-dragging');
      } else if (pointers.size === 2) {
        const pts = Array.from(pointers.values());
        pinch = {dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), scale: scale};
      }
    });
    stage.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
      if (pointers.size >= 2 && pinch) {
        const pts = Array.from(pointers.values());
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (pinch.dist) setScale(pinch.scale * (dist / pinch.dist));
        return;
      }
      if (!drag || scale <= 1) return;
      tx = drag.tx + (event.clientX - drag.x);
      ty = drag.ty + (event.clientY - drag.y);
      clampPan();
      apply();
    });
    function endPointer(event) {
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinch = null;
      if (pointers.size === 0) {
        drag = null;
        stage.classList.remove('is-dragging');
      }
    }
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    document.addEventListener('keydown', (event) => {
      if (root.hidden) return;
      if (event.key === 'Escape') close();
      else if (event.key === '+' || event.key === '=') setScale(scale * 1.35);
      else if (event.key === '-' || event.key === '_') setScale(scale / 1.35);
      else if (event.key === 'ArrowLeft') load(index - 1);
      else if (event.key === 'ArrowRight') load(index + 1);
    });

    return {open: open, close: close, get index() { return index; }};
  }

  function initGallery(gallery) {
    if (!markInit(gallery)) return;
    const slides = qsa('[data-gallery-slide]', gallery);
    const thumbs = qsa('[data-gallery-thumb]', gallery);
    const prevBtn = qs('[data-gallery-prev]', gallery);
    const nextBtn = qs('[data-gallery-next]', gallery);
    const items = slides.length ? slides : thumbs;
    if (items.length === 0) return;
    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    function warm(index) {
      const image = slides[(index + slides.length) % slides.length]?.querySelector('img');
      if (!image) return;
      image.loading = 'eager';
      if (image.decode) image.decode().catch(() => {});
    }

    function show(index) {
      if (!items.length) return;
      active = (index + items.length) % items.length;
      slides.forEach((slide, i) => {
        const on = i === active;
        slide.classList.toggle('is-active', on);
        if (on) slide.setAttribute('data-gallery-main', '');
        else slide.removeAttribute('data-gallery-main');
      });
      thumbs.forEach((thumb, i) => {
        const on = i === active;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-current', on ? 'true' : 'false');
      });
      warm(active);
      warm(active + 1);
      warm(active - 1);
      const current = slides[active];
      const currentIsVideo = current && (current.getAttribute('data-media-type') === 'video' || current.getAttribute('data-media-type') === 'external_video');
      if (zoomBtn) zoomBtn.hidden = !!currentIsVideo;
      slides.forEach((slide) => {
        if (slide === current) return;
        slide.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
      });
    }

    slides.forEach((slide) => warm(slides.indexOf(slide)));

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    if (prevBtn) prevBtn.addEventListener('click', () => show(active - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => show(active + 1));

    const watchBtn = qs('[data-gallery-watch]', gallery);
    if (watchBtn) {
      watchBtn.addEventListener('click', () => {
        const videoIndex = slides.findIndex((slide) => {
          const type = slide.getAttribute('data-media-type');
          return type === 'video' || type === 'external_video';
        });
        if (videoIndex >= 0) show(videoIndex);
      });
    }

    const zoomBtn = qs('[data-gallery-zoom]', gallery);
    const zoom = buildZoomViewer(slides, () => active, (index) => show(index));
    slides.forEach((slide) => {
      slide.addEventListener('click', (event) => {
        if (event.target.closest('.product-gallery__nav, .product-gallery__zoom, video, iframe')) return;
        const type = slide.getAttribute('data-media-type');
        if (type === 'video' || type === 'external_video') return;
        zoom.open(active);
      });
    });
    if (zoomBtn) {
      zoomBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        zoom.open(active);
      });
    }

    gallery.addEventListener('gallery:show-src', (event) => {
      const src = event.detail;
      const match = items.findIndex((el) => el.getAttribute('data-full-src') === src);
      if (match >= 0) show(match);
    });
  }

  function initQuantityField(field) {
    if (!markInit(field)) return;
    const input = qs('input', field);
    qsa('[data-qty-step]', field).forEach((btn) => {
      btn.addEventListener('click', () => {
        const delta = parseInt(btn.getAttribute('data-qty-step'), 10);
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
      });
    });
  }

  function initTabs(root) {
    if (!markInit(root)) return;
    const buttons = qsa('[data-tab-button]', root);
    const panels = qsa('[data-tab-panel]', root);
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-tab-button');
        buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
        panels.forEach((p) => {
          p.hidden = p.getAttribute('data-tab-panel') !== target;
        });
      });
    });
  }

  function initViewToggle(root) {
    if (!markInit(root)) return;
    const buttons = qsa('[data-view-button]', root);
    const grid = qs('[data-product-grid]', root.closest('.collection-layout') || document);
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const view = btn.getAttribute('data-view-button');
        buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
        if (grid) grid.classList.toggle('product-grid--list', view === 'list');
      });
    });
  }

  function initRecommendations(el) {
    if (!markInit(el)) return;
    const url = el.getAttribute('data-url');
    if (!url) return;
    fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-product-recommendations]');
        if (fresh && fresh.innerHTML.trim()) el.outerHTML = fresh.outerHTML;
      })
      .catch(() => {});
  }

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-collection-sort]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-filters-toggle]');
    if (!toggle) return;
    const panel = qs('[data-filters-panel]');
    if (!panel) return;
    const open = panel.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  function applyFaqFilter() {
    const activeTab = qs('.faq-tabs [data-faq-tab].is-active');
    const category = activeTab ? activeTab.getAttribute('data-faq-tab') : 'All';
    const searchEl = qs('[data-faq-search]');
    const term = searchEl ? searchEl.value.trim().toLowerCase() : '';
    qsa('[data-faq-item]').forEach((item) => {
      const matchesCategory = category === 'All' || item.getAttribute('data-faq-category') === category;
      const text = (item.getAttribute('data-faq-text') || '').toLowerCase();
      item.hidden = !(matchesCategory && (term === '' || text.includes(term)));
    });
  }

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-faq-tab]');
    if (!tab) return;
    const category = tab.getAttribute('data-faq-tab');
    qsa('[data-faq-tab]').forEach((t) => t.classList.toggle('is-active', t.getAttribute('data-faq-tab') === category));
    applyFaqFilter();
    if (tab.closest('.help-hero__topics')) {
      const faq = qs('.faq-section');
      if (faq) faq.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  });
  document.addEventListener('input', (event) => {
    if (event.target.closest('[data-faq-search]')) applyFaqFilter();
  });
})();
